import React from 'react';
import { motion } from 'framer-motion';
import RefineToolbar from './RefineToolbar';
import { ClinicalGuides } from '../../App';

interface RefineImageViewerProps {
    originalImage: { file: File, url: string };
    isUpscaled: boolean;
    handleUpscaleImage: () => void;
    setIsGumLineEditorOpen: (isOpen: boolean) => void;
    gumLinePath: { x: number; y: number }[] | null;
    setIsClinicalGuideEditorOpen: (isOpen: boolean) => void;
    clinicalGuides: ClinicalGuides | null;
    setIsMouthAnnotatorOpen: (isOpen: boolean) => void;
    mouthPath: { x: number; y: number }[] | null;
    setIsCroppingOneToOne: (isOpen: boolean) => void;
}

const RefineImageViewer: React.FC<RefineImageViewerProps> = ({
    originalImage,
    isUpscaled,
    handleUpscaleImage,
    setIsGumLineEditorOpen,
    gumLinePath,
    setIsClinicalGuideEditorOpen,
    clinicalGuides,
    setIsMouthAnnotatorOpen,
    mouthPath,
    setIsCroppingOneToOne,
}) => {
    const areGuidesDefined = clinicalGuides && (clinicalGuides.midline || clinicalGuides.interpupillary || clinicalGuides.smileArc);

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="w-full h-full relative flex items-center justify-center"
        >
            <motion.div
                key="viewer"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="relative w-full h-full rounded-[2rem] overflow-hidden bg-white dark:bg-zinc-900/50 border border-white/50 dark:border-white/10 shadow-glass dark:shadow-glass-dark backdrop-blur-xl p-4 sm:p-6"
            >
                <div className="relative w-full h-full flex items-center justify-center rounded-2xl overflow-hidden bg-zinc-100/50 dark:bg-black/40 ring-1 ring-black/5 dark:ring-white/5">
                    {/* Grid Background */}
                    <div className="absolute inset-0 opacity-30 pointer-events-none"
                        style={{
                            backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(148, 163, 184, 0.15) 1px, transparent 0)',
                            backgroundSize: '24px 24px'
                        }}
                    />

                    <img
                        src={originalImage.url}
                        alt="Patient's smile before"
                        className="object-contain w-full h-full max-h-full rounded-lg shadow-2xl"
                    />

                    {/* Badge */}
                    <div className="absolute top-6 left-6 bg-white/80 dark:bg-black/60 backdrop-blur-md text-zinc-900 dark:text-white text-[10px] font-bold uppercase px-4 py-2 rounded-full pointer-events-none tracking-[0.2em] shadow-lg border border-white/20 dark:border-white/10 flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-teal-500 animate-pulse" />
                        Original Photo
                    </div>

                    {/* Toolbar */}
                    <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
                        <RefineToolbar
                            mouthPath={mouthPath}
                            setIsMouthAnnotatorOpen={setIsMouthAnnotatorOpen}
                            isUpscaled={isUpscaled}
                            handleUpscaleImage={handleUpscaleImage}
                            gumLinePath={gumLinePath}
                            setIsGumLineEditorOpen={setIsGumLineEditorOpen}
                            areGuidesDefined={!!areGuidesDefined}
                            setIsClinicalGuideEditorOpen={setIsClinicalGuideEditorOpen}
                            setIsCroppingOneToOne={setIsCroppingOneToOne}
                        />
                    </div>
                </div>
            </motion.div>
        </motion.div>
    );
};

export default RefineImageViewer;
